"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "What materials do you specialize in?",
    answer: "We specialize in high-performance industrial laminates, specifically phenolic resins and composite materials designed for extreme durability and chemical resistance in manufacturing environments."
  }, {
    question: "Do you provide custom fabrication services?",
    answer: "Yes. Our facility is equipped for precision CNC machining and custom fabrication to meet your exact specifications. We handle:",
    bullets: ["Precision sheet cutting and routing", "Custom component assembly", "Surface finishing and coating", "Prototyping for industrial applications"]
  }, {
    question: "What is the typical lead time for orders?",
    answer: "Standard stock orders typically ship within 3-5 business days. For custom-fabricated components, lead times range from 2-4 weeks depending on complexity and material availability."
  }, {
    question: "How is pricing determined for industrial orders?",
    answer: "Pricing is based on material grade, volume, and fabrication requirements. We provide transparent, competitive quotes for all bulk orders and long-term supply contracts with no hidden surcharges."
  }, {
    question: "Do you offer material testing and certification?",
    answer: "Absolutely. All our phenolic products meet international industrial standards. We provide full material certification and technical data sheets upon request to ensure compliance with your project requirements."
  }, {
    question: "What industries do you serve?",
    answer: "We serve a wide range of sectors including aerospace, automotive, electrical engineering, and chemical processing. If you have a specific application, our engineering team is ready to assist."
  }, {
    question: "Can you handle large-scale production runs?",
    answer: "Yes. We are equipped to handle everything from small-batch prototyping to high-volume industrial production runs with consistent quality control across every unit."
  }, {
    question: "What if my project specifications change?",
    answer: "We understand that industrial projects evolve. We allow for specification adjustments up until the production phase begins. For changes during production, we will work with you to minimize impact."
  }, {
    question: "Do you provide technical support for material selection?",
    answer: "Yes. Our technical experts are available to help you select the right grade of phenolic or composite material based on your thermal, electrical, and mechanical requirements."
  }, {
    question: "How do I request a formal quote?",
    answer: "You can submit your project requirements through our online portal or contact our sales team directly for a detailed consultation. We ensure accurate, reliable pricing for every inquiry."
  }];
  return <section data-section-id="701" id="faq" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="inline-block text-sm font-semibold uppercase tracking-widest text-primary mb-6">
                  FAQ
                </span>
                <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-tight text-foreground mb-6">
                  Technical insights, clarified.
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
                  Everything you need to know about our industrial materials, fabrication capabilities, and engineering support services.
                </p>

                {}
                <div className="bg-card rounded-lg p-6 shadow-md border border-border mb-6">
                  <div className="flex items-start gap-4">
                    <SafeHydrate><Avatar className="size-12 shrink-0">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_support_expert.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_support_expert.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_support_expert.png-wvc-sizes" alt="Industrial support specialist" />
                      <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                        TS
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="flex-1">
                      <p className="font-default font-semibold text-foreground mb-1">
                        Need technical assistance?
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">
                        Our engineering team is ready to discuss your project requirements.
                      </p>
                      <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors group">
                        Contact our engineers
                        <ArrowRight className="size-4 text-primary transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>

                {}
                <Button asChild size="lg" variant="outline" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground">
                  <Link to="/ContactPage">
                    <Phone className="size-4 text-primary" />
                    Call (555) 987-6543
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group/item data-[state=open]:border-l-[3px] data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300">
                  <AccordionTrigger className="py-6 hover:no-underline [&>svg]:hidden group">
                    <span className="font-default text-lg md:text-xl font-semibold text-foreground text-left pr-6 leading-snug">
                      {faq.question}
                    </span>
                    <span aria-hidden="true" className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-data-[state=open]:bg-primary group-data-[state=open]:text-primary-foreground">
                      <span className="absolute h-0.5 w-4 bg-current rounded" />
                      <span className="absolute h-0.5 w-4 bg-current rounded transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=closed]:rotate-0 rotate-90" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-12">
                    <p className="text-base text-muted-foreground leading-[1.7]">
                      {faq.answer}
                    </p>
                    {faq.bullets && <ul className="mt-4 space-y-2">
                        {faq.bullets.map((bullet, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-base text-muted-foreground leading-[1.7]">
                            <span className="mt-2.5 size-1.5 shrink-0 rounded-lg bg-primary" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}